import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@/utility/localstorage';

import css from './PriceTransparencyPopover.module.css';

const STORAGE_KEY = 'price-transparency-popover-shown';

interface PriceTransparencyPopoverProps {
  isMapOpen?: boolean;
}

export const PriceTransparencyPopover = ({ isMapOpen }: PriceTransparencyPopoverProps) => {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [showNotification, setShowNotification] = useState(false);

  // Get query parameters to check for dates
  const queryParams = useSelector(getQueryParams);
  const hasDates = !!queryParams[ESearchFilters.DATE_FROM] && !!queryParams[ESearchFilters.DATE_TO];

  const popperOptions = {
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 10,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom-start'], // Only allow bottom variations
          allowedAutoPlacements: ['bottom-start'], // Only allow bottom placements
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
          altAxis: false,
        },
      },
    ],
    placement: 'bottom-start' as const,
  };

  const {
    styles: popperStyles,
    attributes,
    forceUpdate,
  } = usePopper(containerEl, popperElement, popperOptions);

  // Show notification after a delay to allow layout to settle
  useEffect(() => {
    const hasBeenShown = Boolean(getItemFromLocalStorage(STORAGE_KEY));
    if (!hasBeenShown && hasDates && !isMapOpen) {
      const timer = setTimeout(() => {
        setShowNotification(true);
        // Force update popper position when notification shows
        if (forceUpdate) {
          forceUpdate();
        }
      }, 500); // Wait for layout to settle

      return () => clearTimeout(timer);
    }
  }, [hasDates, isMapOpen, forceUpdate]);

  // Hide notification when map is open
  useEffect(() => {
    if (isMapOpen) {
      setShowNotification(false);
    }
  }, [isMapOpen]);

  const handleClose = useCallback(() => {
    setShowNotification(false);
    setItemInLocalStorage(STORAGE_KEY, 'true');
  }, []);

  // Early return if notification shouldn't be shown
  if (!showNotification || !hasDates || isMapOpen) return null;

  return (
    <>
      <div ref={setContainerEl} />
      {createPortal(
        <div
          ref={setPopperElement}
          style={{ ...popperStyles.popper, zIndex: 2 }}
          {...attributes.popper}>
          <div className="p-4 text-white bg-gray-800 rounded-lg w-fit max-w-[258px]">
            <div ref={setArrowElement} className={css.arrow} />
            <div className="flex items-center gap-2">
              <Text
                variant={ETextStyleVariant.Label}
                className="text-white rounded-lg p-[6px] bg-[#B95B46]">
                <FormattedMessage defaultMessage="NEW" id="5Jcaq4" />
              </Text>
              <Text variant={ETextStyleVariant.SmallRegular}>
                <FormattedMessage defaultMessage="Price Transparency" id="Kfg+5c" />
              </Text>
            </div>
            <div>
              <Text className="py-2" variant={ETextStyleVariant.LegalRegular}>
                <FormattedMessage
                  defaultMessage="Estimated price includes all fees and insurance. Tax will be added at checkout."
                  id="pubeDk"
                />
              </Text>
            </div>
            <div>
              <Text
                variant={ETextStyleVariant.LegalLink}
                onClick={handleClose}
                className="cursor-pointer w-fit">
                <FormattedMessage defaultMessage="Got it" id="NYTGIb" />
              </Text>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};
